/**
 * @fileoverview Estilos do modal "Compartilhar quadro": o visual dos diálogos do
 * app (StartConversationModal), neutro nos dois temas, com cor só no estado do
 * link (ativo, expirado, revogado).
 */

import { makeStyles } from "@material-ui/core/styles";
import { modernFieldStyles } from "../ModernSelect";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const textPrimary = dark ? "rgba(255,255,255,0.92)" : "#0f172a";
  const textSecondary = dark ? "rgba(255,255,255,0.64)" : "#475569";
  const textMuted = dark ? "rgba(255,255,255,0.45)" : "#64748b";
  const hover = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.045)";
  const line = dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
  const inputBorder = dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)";
  const surface = dark ? "rgba(255,255,255,0.03)" : "#f8fafc";
  const t = "all 160ms cubic-bezier(0.4, 0, 0.2, 1)";
  const xs = theme.breakpoints.down("xs");
  const btn = {
    height: 38,
    padding: "0 16px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: t,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    whiteSpace: "nowrap",
    "&:disabled": { opacity: 0.55, cursor: "not-allowed", boxShadow: "none" },
    "& svg": { width: 16, height: 16, flexShrink: 0 },
  };
  const state = {
    active: { color: dark ? "#4ade80" : "#15803d", bg: dark ? "rgba(74,222,128,0.12)" : "rgba(21,128,61,0.08)" },
    expired: { color: dark ? "#fbbf24" : "#b45309", bg: dark ? "rgba(251,191,36,0.12)" : "rgba(180,83,9,0.08)" },
    revoked: { color: dark ? "#f87171" : "#b91c1c", bg: dark ? "rgba(248,113,113,0.12)" : "rgba(185,28,28,0.07)" },
  };

  return {
    paper: {
      borderRadius: 18,
      background: dark ? "#1c1e23" : "#ffffff",
      width: "100%",
      maxWidth: 560,
      margin: 16,
      maxHeight: "calc(100% - 32px)",
      display: "flex",
      flexDirection: "column",
      boxShadow: dark
        ? "0 24px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)"
        : "0 24px 48px -12px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.04)",
      [xs]: { margin: 8, maxHeight: "calc(100% - 16px)", borderRadius: 16 },
    },
    header: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 20px 0", flexShrink: 0 },
    headIcon: {
      width: 36,
      height: 36,
      borderRadius: 10,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: hover,
      color: textSecondary,
      "& svg": { width: 18, height: 18 },
    },
    headText: { flex: 1, minWidth: 0 },
    title: { fontSize: 17, fontWeight: 600, color: textPrimary, lineHeight: 1.35, margin: 0 },
    subtitle: { fontSize: 13.5, color: textSecondary, lineHeight: 1.5, margin: "2px 0 0" },
    boardName: { color: textPrimary, fontWeight: 500 },
    iconBtn: {
      background: "transparent",
      border: "none",
      padding: 6,
      cursor: "pointer",
      color: textMuted,
      display: "inline-flex",
      borderRadius: 8,
      transition: t,
      "&:hover:not(:disabled)": { color: textPrimary, background: hover },
      "&:disabled": { opacity: 0.4, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${textMuted}`, outlineOffset: 1 },
      "& svg": { width: 18, height: 18 },
    },
    close: { margin: "-6px -6px 0 0" },
    body: {
      padding: "18px 20px 8px",
      display: "flex",
      flexDirection: "column",
      gap: 18,
      overflowY: "auto",
      flex: 1,
      minHeight: 0,
    },
    section: { display: "flex", flexDirection: "column", gap: 8 },
    sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
    label: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: textMuted,
      margin: 0,
    },
    hint: { fontSize: 12.5, color: textMuted, lineHeight: 1.5, margin: 0 },
    text: { fontSize: 13.5, color: textSecondary, lineHeight: 1.55, margin: 0 },
    error: { fontSize: 12.5, color: dark ? "#fca5a5" : "#b91c1c", lineHeight: 1.5, margin: 0 },
    field: { ...modernFieldStyles(theme), "& .MuiInputBase-root": { fontSize: 14 } },

    segmented: {
      display: "inline-flex",
      padding: 3,
      gap: 3,
      borderRadius: 11,
      border: `1px solid ${line}`,
      background: surface,
      alignSelf: "flex-start",
      [xs]: { alignSelf: "stretch" },
    },
    segment: {
      ...btn,
      height: 32,
      padding: "0 14px",
      fontSize: 13,
      border: "none",
      background: "transparent",
      color: textSecondary,
      [xs]: { flex: 1 },
      "&:hover:not(:disabled)": { color: textPrimary },
    },
    segmentActive: {
      background: dark ? "rgba(255,255,255,0.10)" : "#ffffff",
      color: textPrimary,
      boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,0.08)",
    },
    chips: { display: "flex", flexWrap: "wrap", gap: 6 },
    chip: {
      ...btn,
      height: 30,
      padding: "0 12px",
      fontSize: 12.5,
      borderRadius: 999,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textSecondary,
      "&:hover:not(:disabled)": { background: hover, color: textPrimary },
    },
    chipActive: {
      borderColor: dark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.55)",
      color: textPrimary,
      background: hover,
    },
    check: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      cursor: "pointer",
      userSelect: "none",
      "& .MuiCheckbox-root": { padding: 0, marginTop: 1, color: textMuted },
      "& .MuiCheckbox-colorPrimary.Mui-checked": { color: dark ? "#e5e7eb" : "#0f172a" },
    },
    checkText: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    checkTitle: { fontSize: 14, fontWeight: 500, color: textPrimary, lineHeight: 1.4 },
    // O rótulo do campo fica acima da borda: um respiro do texto de cima.
    passwordRow: { display: "flex", alignItems: "flex-start", gap: 6, paddingTop: 6 },
    passwordActions: { display: "flex", gap: 2, paddingTop: 6 },

    // Link criado: o endereço com o botão de copiar.
    success: { display: "flex", alignItems: "center", gap: 10 },
    successIcon: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: state.active.color,
      background: state.active.bg,
      "& svg": { width: 18, height: 18 },
    },
    linkBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 6px 6px 12px",
      borderRadius: 12,
      border: `1px solid ${inputBorder}`,
      background: surface,
      minWidth: 0,
    },
    linkText: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      color: textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      border: "none",
      background: "transparent",
      outline: "none",
      padding: 0,
    },
    facts: { display: "flex", flexWrap: "wrap", gap: 6 },
    fact: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      height: 24,
      padding: "0 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      color: textSecondary,
      background: hover,
      "& svg": { width: 13, height: 13 },
    },

    // Lista dos links do quadro.
    list: { display: "flex", flexDirection: "column", border: `1px solid ${line}`, borderRadius: 14, overflow: "hidden" },
    row: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "12px 12px 12px 14px",
      "& + &": { borderTop: `1px solid ${line}` },
    },
    rowMain: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 },
    rowTitle: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    rowName: {
      fontSize: 14,
      fontWeight: 600,
      color: textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    rowMeta: { fontSize: 12.5, color: textMuted, lineHeight: 1.5, overflowWrap: "anywhere" },
    rowInactive: { "& $rowName": { color: textSecondary } },
    rowActions: { display: "flex", alignItems: "center", gap: 2, flexShrink: 0 },
    stateChip: {
      display: "inline-flex",
      alignItems: "center",
      height: 20,
      padding: "0 8px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      flexShrink: 0,
    },
    stateActive: { color: state.active.color, background: state.active.bg },
    stateExpired: { color: state.expired.color, background: state.expired.bg },
    stateRevoked: { color: state.revoked.color, background: state.revoked.bg },
    empty: { fontSize: 13.5, color: textMuted, textAlign: "center", padding: "18px 12px", margin: 0 },
    loading: { display: "flex", justifyContent: "center", padding: 24, color: textMuted },
    menuPaper: {
      borderRadius: 12,
      background: dark ? "#18181b" : "#ffffff",
      border: `1px solid ${line}`,
      boxShadow: dark ? "0 18px 40px rgba(0,0,0,0.45)" : "0 18px 40px rgba(15,23,42,0.12)",
      "& .MuiMenuItem-root": { fontSize: 13.5, minHeight: 36, borderRadius: 8, margin: "0 4px", color: textPrimary, gap: 10 },
      "& .MuiMenuItem-root svg": { width: 15, height: 15, color: textMuted },
    },
    menuDanger: { "&.MuiMenuItem-root": { color: state.revoked.color }, "&.MuiMenuItem-root svg": { color: state.revoked.color } },

    footer: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 8,
      padding: "14px 20px 20px",
      flexShrink: 0,
      flexWrap: "wrap",
      borderTop: `1px solid ${line}`,
    },
    footerSpacer: { flex: 1 },
    btnGhost: {
      ...btn,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textSecondary,
      "&:hover:not(:disabled)": { background: hover, color: textPrimary },
    },
    btnDanger: {
      ...btn,
      border: "1px solid transparent",
      background: "transparent",
      color: state.revoked.color,
      paddingLeft: 10,
      paddingRight: 10,
      "&:hover:not(:disabled)": { background: state.revoked.bg },
    },
    btnPrimary: {
      ...btn,
      minWidth: 128,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      boxShadow: dark ? "0 6px 14px -4px rgba(37,99,235,0.45)" : "0 6px 14px -4px rgba(59,130,246,0.45)",
      "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
      "&:focus-visible": { outline: "2px solid #93b4ff", outlineOffset: 2 },
    },
    btnSmall: { height: 32, padding: "0 12px", fontSize: 13 },
    copied: { color: state.active.color, "&:hover:not(:disabled)": { color: state.active.color } },
  };
});

export default useStyles;
